/** SUNTRONIX ONE — Caller Popup (incoming call screen-pop).
 * Renders an identified caller (or UNKNOWN) with context + quick actions. */
import { useNavigate } from "react-router-dom";
import {
  PhoneIncoming, User, Building2, Wrench, Truck, FileText, Ticket,
  UserPlus, StickyNote, X, ExternalLink,
} from "lucide-react";

const TYPE_META = {
  CONTACT: { icon: User, tint: "bg-blue-100 text-blue-800", label: "Contact" },
  CUSTOMER: { icon: Building2, tint: "bg-emerald-100 text-emerald-800", label: "Customer" },
  EMPLOYEE: { icon: User, tint: "bg-violet-100 text-violet-800", label: "Employee" },
  VENDOR: { icon: Truck, tint: "bg-amber-100 text-amber-800", label: "Vendor" },
  PARTNER: { icon: Building2, tint: "bg-cyan-100 text-cyan-800", label: "Partner" },
  UNKNOWN: { icon: User, tint: "bg-slate-200 text-slate-700", label: "Unknown" },
};

const CountPill = ({ icon: Icon, label, value, testId }) => (
  <div
    data-testid={testId}
    className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5"
  >
    <Icon className="h-3.5 w-3.5 text-slate-500" />
    <span className="text-[10px] text-slate-500">{label}</span>
    <span className="ml-auto text-[12px] font-semibold text-slate-900">{value ?? 0}</span>
  </div>
);

export const CallerPopup = ({ caller, onClose, onAction }) => {
  const navigate = useNavigate();
  if (!caller) return null;
  const meta = TYPE_META[caller.match_type] || TYPE_META.UNKNOWN;
  const Icon = meta.icon;
  const c = caller.counts || {};
  const known = caller.matched;

  const act = (key) => {
    if (key === "OPEN_CUSTOMER_360" && caller.customer_360_url) {
      navigate(caller.customer_360_url);
      onClose?.();
      return;
    }
    onAction?.(key, caller);
  };

  const actionBtns = known
    ? [
        ["OPEN_CUSTOMER_360", "Open Customer 360", ExternalLink],
        ["CREATE_TICKET", "Create Ticket", Ticket],
        ["CREATE_FOLLOWUP", "Create Follow-up", FileText],
        ["ADD_NOTE", "Add Note", StickyNote],
      ]
    : [
        ["CREATE_CONTACT", "Create Contact", UserPlus],
        ["CREATE_CUSTOMER", "Create Customer", Building2],
        ["CREATE_TICKET", "Create Ticket", Ticket],
        ["ADD_NOTE", "Add Note", StickyNote],
      ];

  return (
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center bg-slate-900/40 p-4 pt-16 sm:pt-24"
      data-testid="caller-popup-overlay"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200"
        data-testid="caller-popup"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 bg-teal-900 px-4 py-3 text-white">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-white/15">
            <PhoneIncoming className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wide text-teal-200">Incoming call</div>
            <div className="font-mono text-[13px]">{caller.phone}</div>
          </div>
          <button onClick={onClose} data-testid="caller-popup-close" className="text-teal-200 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <div className="flex items-center gap-3">
            <div className={`grid h-11 w-11 place-items-center rounded-xl ${meta.tint}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-semibold text-slate-900" data-testid="caller-popup-name">
                {caller.name}
              </div>
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${meta.tint}`}>
                  {meta.label}
                </span>
                {caller.role && <span className="truncate">{caller.role}</span>}
                {caller.company_name && caller.match_type === "CONTACT" && (
                  <span className="truncate">· {caller.company_name}</span>
                )}
              </div>
            </div>
          </div>

          {known && caller.match_type !== "EMPLOYEE" && (
            <div className="grid grid-cols-2 gap-2" data-testid="caller-popup-counts">
              <CountPill icon={FileText} label="Open Quotes" value={c.open_quotes} testId="cp-open-quotes" />
              <CountPill icon={Building2} label="Orders" value={c.orders} testId="cp-orders" />
              <CountPill icon={Truck} label="In Transit" value={c.shipments_in_transit} testId="cp-shipments" />
              <CountPill icon={Ticket} label="Open Tickets" value={c.open_tickets} testId="cp-tickets" />
            </div>
          )}

          {!known && (
            <div className="rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="caller-popup-unknown">
              This number isn't in the system yet. Create a record — duplicates are never made automatically.
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 pt-1">
            {actionBtns.map(([key, label, A]) => (
              <button
                key={key}
                onClick={() => act(key)}
                data-testid={`caller-action-${key.toLowerCase()}`}
                className="flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-2 text-[11px] font-semibold text-slate-700 hover:border-teal-400 hover:bg-teal-50"
              >
                <A className="h-3.5 w-3.5 text-teal-700" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallerPopup;
